import React from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import type { BatchData, Connection } from 'app/types';
import type { TableCellHeaderSortOptions } from 'app/components';
import connectionService from '../../../Domain/Connections/Services/ConnectionService';
import { useSnakeBar } from '../../useSnakeBar';

type GetBatchCompareDataQueryVariables = {
  batchId: string | undefined;
  connectionId: Connection['connectionId'] | undefined;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  mutationType?: string;
  sortBy?: Record<string, TableCellHeaderSortOptions>;
};

type GetBatchCompareDataQueryResultData = {
  data: Array<BatchData>;
  totalCount: number;
};

type UseQueryWrapperArgs<TQueryVariables> = {
  variables: TQueryVariables;
  options?: any;
};

const queryFn =
  ({ batchId, connectionId, ...others }: GetBatchCompareDataQueryVariables) =>
  async () => {
    if (batchId && connectionId) {
      const response = await connectionService.getBatchCompareData({
        batchId,
        connectionId,
        ...others,
      });
      return response.data;
    }
  };

const QUERY_KEY = 'getBatchCompareData';

export const useConnectionGetBatchCompareDataQuery = ({
  variables,
  options,
}: UseQueryWrapperArgs<GetBatchCompareDataQueryVariables>): UseQueryResult<
  GetBatchCompareDataQueryResultData,
  AxiosError
> => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(
    (error: unknown) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status !== 422 && error.response?.status !== 404) {
          showErrorSnakeBar({ method: QUERY_KEY });
        }
      } else {
        showErrorSnakeBar({ method: QUERY_KEY });
      }
    },
    [showErrorSnakeBar],
  );

  const select = React.useCallback(data => {
    return {
      data: data['hydra:member'],
      totalCount: data['hydra:totalItems'],
    };
  }, []);

  return useQuery<GetBatchCompareDataQueryResultData, AxiosError>(
    [QUERY_KEY, ...Object.values(variables || {})],
    queryFn(variables),
    {
      select,
      onError,
      retry: (failureCount: number, error: AxiosError) => {
        return error.response?.status !== 422 && error.response?.status !== 404 && failureCount < 3;
      },
      ...(options || {}),
    },
  );
};
